// What the player predicted, and when.
//
// A separate store from `src/state/store.ts` on purpose, for the same reason the debrief has its own:
// this is a record of the human's judgment during a fight, not simulation state, nothing in the sim
// reads it, and it has to survive the transition to the debrief stage where the store's live snapshot
// does not. One fight, one set of forecasts, scored at the end.
//
// The point of the record is Brier scoring, which is the only honest way to tell somebody their
// judgment is miscalibrated. "You were overconfident" is an opinion; "you said 70% three times and it
// happened once" is a fact about their own numbers, and it is the thing the debrief panel says.

import { create } from 'zustand';

/** The judgments the player is asked to make. Deliberately the same two the estimator answers, so the
 * debrief can put the human and the machine on one reliability strip. */
export type ElicitQuestionId = 'reserveCommitSoon' | 'mainEffortThird';

/** The five probabilities a chip offers. Five, not a slider: a slider invites false precision, and the
 * research on forecast elicitation is consistent that coarse buckets are both easier to answer and no
 * worse once scored. 50% is in the middle and is a real answer, not a refusal. */
export const PROB_CHIPS = [0.1, 0.3, 0.5, 0.7, 0.9] as const;

/** What the estimator said, copied at the moment the player committed. Stored rather than recomputed so
 * the debrief scores the number that was actually on the card. */
export interface RecordedEstimate {
  /** The estimator's answer as a string: 'yes'/'no', or a third name. */
  value: string;
  /** The probability the estimator put on the thing being asked about (yes, or the player's option). */
  probability: number;
  confidence: number;
  provider: 'heuristic' | 'jev';
  calibrated: boolean;
  latencyMs: number;
  /** Fights the calibration was fitted on, for the badge. Zero when uncalibrated. */
  calibratedOnRuns: number;
}

export interface ElicitAnswer {
  /** Sim tick the question was asked at — what the debrief resolves the outcome against. */
  t: number;
  question: ElicitQuestionId;
  /** The question as the player read it, so the debrief quotes it rather than paraphrasing. */
  prompt: string;
  /** For a choice question, the option the player picked. Undefined for a yes/no. */
  playerChoice?: string;
  /** The probability the player asserted, 0..1. For a choice, the probability on `playerChoice`. */
  playerProb: number;
  estimatorEstimate: RecordedEstimate;
  /** The event that raised the question, so the replay can scrub to it. */
  triggerEventId?: number;
}

interface ElicitState {
  answers: ElicitAnswer[];
  /** Event ids already turned into a question, so one contact report is never asked about twice. */
  asked: number[];
  record: (answer: ElicitAnswer) => void;
  markAsked: (eventId: number) => void;
  hasAsked: (eventId: number) => boolean;
  reset: () => void;
}

export const useElicit = create<ElicitState>((set, get) => ({
  answers: [],
  asked: [],

  record: (answer) => set((s) => ({ answers: [...s.answers, answer] })),

  markAsked: (eventId) => set((s) => (s.asked.includes(eventId) ? s : { asked: [...s.asked, eventId] })),

  hasAsked: (eventId) => get().asked.includes(eventId),

  reset: () => set({ answers: [], asked: [] }),
}));
